<template>
  <li class="request-row" :class="{ 'is-busy': busy, 'is-live': liveActive, 'is-fresh': fresh }">
    <div class="request-main">
      <span class="request-source" :class="'src-' + row.source">{{ sourceLabel }}</span>
      <span class="request-what">
        <strong :title="row.raw_text">
          {{ title }}
          <em v-if="fresh" class="request-new">New</em>
        </strong>
        <small v-if="reason" class="request-reason">
          <span v-if="segment" class="request-segment">{{ segment }}</span>{{ reason }}
        </small>
        <small v-else-if="isVariety" class="request-variety">Variety — asks for brands we do not carry yet</small>
        <small v-if="liveStep && liveStep.detail" class="request-live" :class="'st-' + liveStep.status">{{ liveStep.detail }}</small>
        <small v-else-if="row.resolution_note" class="request-note">{{ row.resolution_note }}</small>
        <small v-if="row.asin">
          <a :href="'https://www.amazon.com/dp/' + row.asin" target="_blank" rel="noopener">View on Amazon ↗</a>
        </small>
      </span>
      <span class="request-demand" :title="'Asked ' + row.demand_count + ' time(s)'">{{ row.demand_count > 1 ? '×' + row.demand_count : '' }}</span>
      <span class="request-status" :class="'st-' + statusKey">
        <span v-if="liveActive" class="request-spinner" aria-hidden="true"></span>{{ statusLabel }}
      </span>
      <span class="request-actions">
        <button
          v-if="liveStep && !liveActive && liveHasDetails"
          type="button"
          class="request-btn"
          @click="detailsOpen = !detailsOpen">{{ detailsOpen ? 'Hide' : 'Details' }}</button>
        <template v-if="!liveActive">
          <button
            v-if="canOnboard"
            type="button"
            class="request-btn request-btn-primary"
            :disabled="busy"
            title="Search Amazon for it and scrape it now"
            @click="$emit('onboard', row)">Onboard now</button>
          <button
            v-if="canQueue"
            type="button"
            class="request-btn"
            :disabled="busy"
            @click="$emit('status', 'queued')">{{ row.status === 'not_found' ? 'Retry' : 'Queue' }}</button>
          <button
            v-if="canReject"
            type="button"
            class="request-btn"
            :disabled="busy"
            @click="$emit('status', 'rejected')">Reject</button>
        </template>
      </span>
    </div>
    <!-- What the dispatch run is doing for this part, live -->
    <job-activity-feed
      v-if="liveStep && liveHasDetails && (liveActive || detailsOpen)"
      class="request-feed"
      :items="live"
      :root="liveStep.id"
      compact />
  </li>
</template>

<script>
import JobActivityFeed from './JobActivityFeed.vue'

const SOURCE_LABELS = {
  manual: 'Asked for',
  ai_suggest: 'AI pick',
  site_search: 'Site search',
  search_console: 'Google',
  feedback: 'Feedback'
}
const STATUS_LABELS = {
  new: 'To triage',
  queued: 'Queued',
  searching: 'Searching',
  onboarded: 'Onboarded',
  already_have: 'Already have',
  not_found: 'Not on Amazon',
  rejected: 'Rejected'
}
/** Outcomes the database already knows; these win over the live feed. */
const SETTLED = ['onboarded', 'already_have', 'not_found', 'rejected']

/**
 * One part request: what was asked for, why, where it stands, and what an
 * admin can do next. While a dispatch run works on it, `live` (that run's
 * activity for this request) turns the row into a progress view.
 */
export default {
  name: 'PartRequestRow',
  components: { JobActivityFeed },
  props: {
    row: { type: Object, required: true },
    busy: { type: Boolean, default: false },
    // Live activity items for this request (services/jobActivity requestActivity).
    live: { type: Array, default: () => [] },
    // Just added (e.g. by an AI suggestion run) — highlighted until reload.
    fresh: { type: Boolean, default: false }
  },
  data () {
    return { detailsOpen: false }
  },
  computed: {
    sourceLabel () {
      return SOURCE_LABELS[this.row.source] || this.row.source || 'Request'
    },
    liveStep () {
      return this.live.find((item) => item.id === `req.${this.row.id}`) || null
    },
    liveActive () {
      return !!this.liveStep && (this.liveStep.status === 'running' || this.liveStep.status === 'waiting')
    },
    liveHasDetails () {
      return !!this.liveStep && this.live.some((item) => item.parent === this.liveStep.id)
    },
    statusKey () {
      if (this.liveStep && !SETTLED.includes(this.row.status)) {
        return { running: 'working', waiting: 'waiting', done: 'onboarded', failed: 'not_found' }[this.liveStep.status] || this.row.status
      }
      return this.row.status
    },
    statusLabel () {
      if (this.liveStep && !SETTLED.includes(this.row.status)) {
        const label = { running: 'Onboarding…', waiting: 'In line', done: 'Done', failed: 'Failed' }[this.liveStep.status]
        if (label) return label
      }
      return STATUS_LABELS[this.row.status] || this.row.status || ''
    },
    title () {
      if (this.row.asin) return `ASIN ${this.row.asin}`
      if (this.row.upc) return `UPC ${this.row.upc}`
      return this.row.raw_text
    },
    evidence () {
      const value = this.row.evidence
      return value && typeof value === 'object' ? value : {}
    },
    /** The AI's case for a suggestion. */
    reason () {
      return this.row.source === 'ai_suggest' ? (this.evidence.reason || '') : ''
    },
    segment () {
      return this.row.source === 'ai_suggest' ? (this.evidence.segment || '') : ''
    },
    /**
     * A request that names a chipset but no brand. Onboarding one of these is
     * variety, not duplication — we may already carry the chipset from another
     * brand and still want this one.
     */
    isVariety () {
      return !!(this.row.family_key && !this.row.brand && !this.row.asin && !this.row.upc)
    },
    canOnboard () {
      return !!this.row.part_type && ['new', 'queued', 'not_found', 'rejected'].indexOf(this.row.status) >= 0
    },
    canQueue () {
      return ['new', 'not_found', 'rejected'].indexOf(this.row.status) >= 0
    },
    canReject () {
      return ['new', 'queued', 'not_found'].indexOf(this.row.status) >= 0
    }
  }
}
</script>

<style scoped>
.request-row {
  display: grid;
  gap: 8px;
  padding: 8px 11px;
  border: 1px solid rgba(67,72,87,0.6);
  border-radius: 6px;
  background: rgba(8, 11, 17, 0.32);
  transition: border-color 0.2s, background 0.2s;
}
.request-row.is-busy { opacity: 0.6; }
.request-row.is-live { border-color: rgba(25,151,198,0.55); background: rgba(25,151,198,0.06); }
.request-row.is-fresh:not(.is-live) { border-color: rgba(196,167,245,0.4); }
.request-main {
  display: grid;
  grid-template-columns: 70px minmax(0, 1fr) 34px 112px auto;
  align-items: center;
  gap: 10px;
}
.request-source {
  color: #1997c6;
  font-size: 7.3pt;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.request-source.src-ai_suggest { color: #c4a7f5; }
.request-what { display: grid; gap: 2px; min-width: 0; }
.request-what strong {
  color: #dfe4ee;
  font-size: 8.8pt;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.request-new {
  margin-left: 6px;
  color: #c4a7f5;
  font-size: 7pt;
  font-style: normal;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.request-what small { color: #8f98aa; font-size: 8pt; line-height: 1.4; }
.request-what a { color: #7fd0ee; text-decoration: none; }
.request-what a:hover { color: #eef8fc; }
.request-reason { color: #b7c2d6 !important; }
.request-live { color: #9bdff5 !important; }
.request-live.st-done { color: #8fe8c4 !important; }
.request-live.st-failed { color: #f49aaa !important; }
.request-segment {
  display: inline-block;
  margin-right: 6px;
  padding: 0 6px;
  border: 1px solid rgba(196,167,245,0.4);
  border-radius: 999px;
  color: #c4a7f5;
  font-size: 7pt;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.request-variety { color: #16b4ed !important; }
.request-demand { color: #8f98aa; font-size: 8.3pt; text-align: right; }
.request-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 2px 8px;
  border: 1px solid rgba(67,72,87,0.8);
  border-radius: 999px;
  color: #b7c2d6;
  font-size: 7.6pt;
  font-weight: 700;
  white-space: nowrap;
}
.request-status.st-queued,
.request-status.st-searching,
.request-status.st-working { border-color: rgba(25,151,198,0.5); color: #7fd0ee; }
.request-status.st-waiting { border-style: dashed; color: #8f98aa; }
.request-status.st-onboarded { border-color: rgba(27,201,142,0.5); color: #8fe8c4; }
.request-status.st-already_have { border-color: rgba(224,168,86,0.5); color: #e0a856; }
.request-status.st-rejected,
.request-status.st-not_found { border-color: rgba(244,154,170,0.45); color: #f49aaa; }
.request-spinner {
  width: 8px;
  height: 8px;
  border: 2px solid rgba(25,151,198,0.3);
  border-top-color: #16b4ed;
  border-radius: 50%;
  animation: request-spin 0.8s linear infinite;
}
@keyframes request-spin { to { transform: rotate(360deg); } }
.request-actions { display: flex; justify-content: flex-end; gap: 6px; white-space: nowrap; }
.request-btn {
  padding: 3px 9px;
  border: 1px solid #434857;
  border-radius: 6px;
  background: transparent;
  color: #9198aa;
  font: inherit;
  font-size: 7.9pt;
  cursor: pointer;
  transition: 0.15s;
}
.request-btn:hover { border-color: #1997c6; color: #cfd2da; }
.request-btn:disabled { opacity: 0.5; cursor: default; }
.request-btn:focus { outline: none; }
.request-btn:focus-visible { outline: 1px solid rgba(25,151,198,0.8); outline-offset: 2px; }
.request-btn-primary { border-color: rgba(25,151,198,0.58); color: #9bdff5; background: rgba(25,151,198,0.08); }
.request-btn-primary:hover { border-color: #1997c6; color: #eef8fc; background: rgba(25,151,198,0.14); }
.request-feed { margin-left: 80px; }
@media (max-width: 900px) {
  .request-main { grid-template-columns: minmax(0, 1fr) auto; row-gap: 6px; }
  .request-source, .request-demand { display: none; }
  .request-actions { grid-column: 1 / -1; justify-content: flex-start; }
  .request-feed { margin-left: 0; }
}
</style>
